import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { bookingsService, availabilityService, coursesService } from '../../../services/api';

/**
 * AddItemForm - Form for adding new rooms, beds, or courses to a booking
 */
function AddItemForm({ booking, entityType, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [availableOptions, setAvailableOptions] = useState([]);

  // Debug: Log when availableOptions changes
  useEffect(() => {
    console.log('Available options updated:', availableOptions);
  }, [availableOptions]);
  
  // Form state
  const [selectedItem, setSelectedItem] = useState(null);
  const [checkIn, setCheckIn] = useState(booking.checkIn?.split('T')[0] || '');
  const [checkOut, setCheckOut] = useState(booking.checkOut?.split('T')[0] || '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [basePrice, setBasePrice] = useState('');
  const [finalAmount, setFinalAmount] = useState('');
  const [notes, setNotes] = useState('');

  // Load available options based on entity type
  const loadAvailableOptions = async () => {
    setLoadingOptions(true);
    try {
      let response;
      const params = {
        checkIn: checkIn + 'T15:00:00.000Z',
        checkOut: checkOut + 'T11:00:00.000Z',
        excludeBookingId: booking.id
      };

      if (entityType === 'room') {
        response = await availabilityService.getRooms(params);
        console.log('Available rooms response:', response);
        // Handle nested data structure: response.data.availableRooms
        const rooms = response.data?.availableRooms || response.availableRooms || [];
        console.log('Extracted rooms:', rooms);
        // Log first room to check structure
        if (rooms.length > 0) {
          console.log('First room structure:', rooms[0]);
          console.log('Room price sources:', {
            'roomType.price': rooms[0].roomType?.price,
            'rate': rooms[0].rate,
            'price': rooms[0].price,
            'roomType': rooms[0].roomType
          });
        }
        setAvailableOptions(rooms);
      } else if (entityType === 'bed') {
        response = await availabilityService.getBeds(params);
        console.log('Available beds response:', response);
        // Handle nested data structure: response.data.availableBeds
        const beds = response.data?.availableBeds || response.availableBeds || [];
        console.log('Extracted beds:', beds);
        // Log first bed to check structure
        if (beds.length > 0) {
          console.log('First bed structure:', beds[0]);
          console.log('Bed price sources:', {
            'price': beds[0].price,
            'roomType.price': beds[0].roomType?.price,
            'roomType.basePrice': beds[0].roomType?.basePrice,
            'roomType.defaultRate': beds[0].roomType?.defaultRate,
            'room.roomType.price': beds[0].room?.roomType?.price,
            'room.roomType.basePrice': beds[0].room?.roomType?.basePrice,
            'room.roomType.defaultRate': beds[0].room?.roomType?.defaultRate,
            'roomType': beds[0].roomType,
            'room': beds[0].room
          });
        }
        setAvailableOptions(beds);
      } else if (entityType === 'course') {
        // For courses, use coursesService
        response = await coursesService.getAll({ upcoming: 'true', limit: 50 });
        console.log('Available courses response:', response);
        // Handle nested data structure: response.data.courses
        const courses = response.data?.courses || response.courses || [];
        console.log('Extracted courses:', courses);
        setAvailableOptions(courses);
      }
    } catch (error) {
      console.error('Error loading available options:', error);
      toast.error(`Failed to load available ${entityType}s`);
      setAvailableOptions([]);
    } finally {
      setLoadingOptions(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedItem) {
      toast.error(`Please select a ${entityType}`);
      return;
    }

    setLoading(true);
    try {
      let response;
      const data = {
        notes: notes.trim() || null
      };

      if (entityType === 'room') {
        data.roomId = selectedItem.id;
        data.checkIn = checkIn + 'T15:00:00.000Z';
        data.checkOut = checkOut + 'T11:00:00.000Z';
        data.basePrice = parseFloat(basePrice) || selectedItem.roomType?.price || 0;
        data.finalAmount = parseFloat(finalAmount) || data.basePrice;
        response = await bookingsService.addRoomToBooking(booking.id, data);
        toast.success('Room added to booking successfully');
      } else if (entityType === 'bed') {
        data.bedId = selectedItem.id;
        data.checkIn = checkIn + 'T15:00:00.000Z';
        data.checkOut = checkOut + 'T11:00:00.000Z';
        data.basePrice = parseFloat(basePrice) || 
                        selectedItem.price || 
                        selectedItem.roomType?.basePrice || 
                        selectedItem.roomType?.defaultRate || 
                        selectedItem.roomType?.price || 
                        selectedItem.room?.roomType?.basePrice || 
                        selectedItem.room?.roomType?.defaultRate || 
                        selectedItem.room?.roomType?.price || 0;
        data.finalAmount = parseFloat(finalAmount) || data.basePrice;
        response = await bookingsService.addBedToBooking(booking.id, data);
        toast.success('Bed added to booking successfully');
      } else if (entityType === 'course') {
        data.courseId = selectedItem.id;
        data.quantity = parseInt(quantity) || 1;
        data.startDate = startDate + 'T09:00:00.000Z';
        data.endDate = endDate + 'T17:00:00.000Z';
        data.fee = parseFloat(basePrice) || selectedItem.fee || 0;
        data.finalAmount = parseFloat(finalAmount) || (data.fee * data.quantity);
        response = await bookingsService.addCourseToBooking(booking.id, data);
        toast.success('Course added to booking successfully');
      }

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error(error.error || error.message || `Failed to add ${entityType}`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate nights for rooms/beds
  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  // Auto-calculate final amount when base price or dates change (for rooms/beds)
  useEffect(() => {
    if ((entityType === 'room' || entityType === 'bed') && basePrice && checkIn && checkOut) {
      const start = new Date(checkIn);
      const end = new Date(checkOut);
      const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      if (nights > 0) {
        const calculatedAmount = parseFloat(basePrice) * nights;
        setFinalAmount(calculatedAmount.toFixed(2));
      }
    }
  }, [basePrice, checkIn, checkOut, entityType]);

  return (
    <div style={{ backgroundColor: '#F5F3EB', padding: '20px', borderRadius: '8px', border: '1px solid #E3DFD3' }}>
      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
        Add {entityType.charAt(0).toUpperCase() + entityType.slice(1)} to Booking
      </h4>

      <form onSubmit={handleSubmit}>
        {/* Date Selection for Rooms/Beds */}
        {(entityType === 'room' || entityType === 'bed') && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                  Check-in Date *
                </label>
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                  Check-out Date *
                </label>
                <input
                  type="date"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            {checkIn && checkOut && (
              <div style={{ fontSize: '13px', color: '#5F6B61', fontStyle: 'italic' }}>
                Duration: {calculateNights()} night{calculateNights() !== 1 ? 's' : ''}
              </div>
            )}
            <button
              type="button"
              onClick={loadAvailableOptions}
              disabled={!checkIn || !checkOut || loadingOptions}
              style={{
                marginTop: '12px',
                backgroundColor: !checkIn || !checkOut || loadingOptions ? '#A5A89B' : '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: !checkIn || !checkOut || loadingOptions ? 'not-allowed' : 'pointer'
              }}
            >
              {loadingOptions ? 'Loading...' : `Load Available ${entityType}s`}
            </button>
          </div>
        )}

        {/* Date Selection for Courses */}
        {entityType === 'course' && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                  End Date *
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={loadAvailableOptions}
              disabled={loadingOptions}
              style={{
                marginTop: '12px',
                backgroundColor: loadingOptions ? '#A5A89B' : '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: loadingOptions ? 'not-allowed' : 'pointer'
              }}
            >
              {loadingOptions ? 'Loading...' : 'Load Available Courses'}
            </button>
          </div>
        )}

        {/* Item Selection */}
        {availableOptions.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
              Select {entityType.charAt(0).toUpperCase() + entityType.slice(1)} *
            </label>
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #D2CDBF', borderRadius: '6px', padding: '8px' }}>
              {availableOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => {
                    console.log('Selected option:', option);
                    setSelectedItem(option);
                    if (entityType === 'room') {
                      // Try multiple price sources: roomType.price, rate, or price field
                      const price = option.roomType?.price || option.rate || option.price || '';
                      console.log('Room price extracted:', price, 'from option:', option);
                      setBasePrice(price);
                      setFinalAmount(price);
                    } else if (entityType === 'bed') {
                      // Try multiple price sources for beds
                      // For shared beds, the price is typically in room.roomType.basePrice or room.roomType.defaultRate
                      const price = option.price || 
                                   option.roomType?.basePrice || 
                                   option.roomType?.defaultRate || 
                                   option.roomType?.price || 
                                   option.room?.roomType?.basePrice || 
                                   option.room?.roomType?.defaultRate ||
                                   option.room?.roomType?.price || 
                                   option.rate || '';
                      console.log('Bed price extracted:', price, 'from option:', option);
                      setBasePrice(price);
                      setFinalAmount(price);
                    } else if (entityType === 'course') {
                      setBasePrice(option.fee || '');
                      setFinalAmount(option.fee * quantity || '');
                    }
                  }}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: selectedItem?.id === option.id ? '#E1E9E2' : 'white',
                    border: selectedItem?.id === option.id ? '2px solid #1A3E2A' : '1px solid #E3DFD3',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                    {entityType === 'room' && `Room ${option.roomNumber} - ${option.roomType?.name || 'N/A'}`}
                    {entityType === 'bed' && `Bed ${option.bedNumber} in Room ${option.room?.roomNumber || 'N/A'} - ${option.roomType?.name || 'N/A'}`}
                    {entityType === 'course' && `${option.name}`}
                  </div>
                  <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '4px' }}>
                    {entityType === 'room' && `₹${option.roomType?.price || option.rate || option.price || 0}/night`}
                    {entityType === 'bed' && `₹${option.price || option.roomType?.basePrice || option.roomType?.defaultRate || option.roomType?.price || option.room?.roomType?.basePrice || option.room?.roomType?.defaultRate || option.room?.roomType?.price || option.rate || 0}/night`}
                    {entityType === 'course' && `₹${option.fee || 0} | ${option.description || ''}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quantity (for courses) */}
        {entityType === 'course' && selectedItem && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
              Quantity *
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => {
                const newQty = parseInt(e.target.value) || 1;
                setQuantity(newQty);
                setFinalAmount(basePrice * newQty || '');
              }}
              min="1"
              required
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
        )}

        {/* Pricing */}
        {selectedItem && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                  Base Price {entityType === 'room' || entityType === 'bed' ? '/night' : ''} *
                </label>
                <input
                  type="number"
                  value={basePrice}
                  onChange={(e) => {
                    setBasePrice(e.target.value);
                    if (entityType === 'course') {
                      setFinalAmount(e.target.value * quantity || '');
                    } else {
                      setFinalAmount(e.target.value);
                    }
                  }}
                  min="0"
                  step="0.01"
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                  Final Amount *
                </label>
                <input
                  type="number"
                  value={finalAmount}
                  onChange={(e) => setFinalAmount(e.target.value)}
                  min="0"
                  step="0.01"
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        {selectedItem && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical'
              }}
              placeholder="Add any notes about this addition..."
            />
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              backgroundColor: 'white',
              color: '#36433B',
              border: '1px solid #D2CDBF',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !selectedItem}
            style={{
              backgroundColor: loading || !selectedItem ? '#A5A89B' : '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading || !selectedItem ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Adding...' : `Add ${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddItemForm;
